import React, { useRef, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import { FeatureCollection } from "geojson";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || "";

interface YachtLocation {
  id: string;
  latitude: number;
  longitude: number;
}
interface CustomPoint {
  type: "Point";
  coordinates: [number, number]; // [longitude, latitude]
}

interface MapboxMapProps {
  yachtLocations: YachtLocation[];
  hoveredLocation: { latitude: number; longitude: number } | null;
  onClusterClick: (clusterYachts: YachtLocation[]) => void;
  clusterYachts: any[];
}

const MapboxMap: React.FC<MapboxMapProps> = ({
  yachtLocations,
  hoveredLocation,
  onClusterClick,
  clusterYachts,
}) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  const fitMapToBounds = (locations: YachtLocation[]) => {
    const validLocations = locations.filter(
      (l) => l.latitude && l.longitude && Math.abs(l.latitude) > 0.01 && Math.abs(l.longitude) > 0.01
    );
    if (validLocations.length > 0 && mapRef.current) {
      const bounds = new mapboxgl.LngLatBounds(
        [validLocations[0].longitude, validLocations[0].latitude],
        [validLocations[0].longitude, validLocations[0].latitude]
      );

      validLocations.forEach((location) => {
        bounds.extend([location.longitude, location.latitude]);
      });

      mapRef.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 12, // Limit max zoom to 12
        // minZoom: 8, // Limit min zoom to 8
        duration: 1000,
      });
    }
  };

  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: "mapbox://styles/mapbox/streets-v11",
        center: [15.0609, 38.1446],
        zoom: 2,
      });

      mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-right");
    }

    mapRef.current?.on("style.load", () => {
      if (!mapRef.current?.getSource("yachts")) {
        const yachtData: FeatureCollection = {
          type: "FeatureCollection",
          features: yachtLocations.map((location) => ({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [location.longitude, location.latitude],
            },
            properties: { id: location.id },
          })),
        };

        mapRef.current?.addSource("yachts", {
          type: "geojson",
          data: yachtData,
          cluster: true,
          clusterMaxZoom: 18, // Increased for better clustering
          clusterRadius: 50,
        });

        // Cluster layer
        mapRef.current?.addLayer({
          id: "clusters",
          type: "circle",
          source: "yachts",
          filter: ["has", "point_count"],
          paint: {
            "circle-color": [
              "step",
              ["get", "point_count"],
              "#51bbd6",
              100,
              "#f1f075",
              750,
              "#f28cb1",
            ],
            "circle-radius": [
              "step",
              ["get", "point_count"],
              20,
              100,
              30,
              750,
              40,
            ],
            "circle-stroke-color": "#fff",
            "circle-stroke-width": 2,
          },
        });

        // Cluster Count Layer
        mapRef.current?.addLayer({
          id: "cluster-count",
          type: "symbol",
          source: "yachts",
          filter: ["has", "point_count"],
          layout: {
            "text-field": "{point_count_abbreviated}",
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12,
          },
          paint: {
            "text-color": "#000",
          },
        });

        // Unclustered Yacht (Single Yacht)
        mapRef.current?.addLayer({
          id: "unclustered-point",
          type: "circle",
          source: "yachts",
          filter: ["!", ["has", "point_count"]],
          paint: {
            "circle-color": "#11b4da",
            "circle-radius": 15,
            "circle-stroke-width": 1,
            "circle-stroke-color": "#fff",
          },
        });

        // Unclustered Yacht Label (Show "1" if single yacht)
        mapRef.current?.addLayer({
          id: "unclustered-label",
          type: "symbol",
          source: "yachts",
          filter: ["!", ["has", "point_count"]],
          layout: {
            "text-field": "1",
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12,
          },
          paint: {
            "text-color": "#000",
          },
        });

        fitMapToBounds(yachtLocations);

        // Change cluster color and cursor on hover
        mapRef.current?.on("mouseenter", "clusters", () => {
          if (mapRef.current) {
            mapRef.current.setPaintProperty(
              "clusters",
              "circle-color",
              "#ff7f50"
            );
            mapRef.current.getCanvas().style.cursor = "pointer";
          }
        });

        mapRef.current?.on("mouseleave", "clusters", () => {
          if (mapRef.current) {
            mapRef.current.setPaintProperty(
              "clusters",
              "circle-color",
              "#f28cb1"
            );
            mapRef.current.getCanvas().style.cursor = "";
          }
        });

        // Cluster click event
        mapRef.current?.on("click", "clusters", (e) => {
          const features = mapRef.current?.queryRenderedFeatures(e.point, {
            layers: ["clusters"],
          });
          if (features?.length) {
            const geometry = features[0].geometry;
            const clusterId = features[0].properties?.cluster_id;
            let clusterCenter: [number, number] =
              geometry.type === "Point"
                ? (geometry as CustomPoint).coordinates
                : [37.7749, -122.4194];

            if (clusterId && mapRef.current?.getSource("yachts")) {
              (
                mapRef.current.getSource("yachts") as mapboxgl.GeoJSONSource
              ).getClusterLeaves(clusterId, Infinity, 0, (err, leaves) => {
                if (!err && leaves) {
                  const clusterYachts = leaves.map((leaf: any) => ({
                    id: leaf.properties.id,
                    latitude: leaf.geometry.coordinates[1],
                    longitude: leaf.geometry.coordinates[0],
                    center: [
                      leaf.geometry.coordinates[1],
                      leaf.geometry.coordinates[0],
                    ],
                    zoom: 14.2,
                    essential: true,
                  }));
                  onClusterClick(clusterYachts);
                  const currentZoom = mapRef.current?.getZoom() || 10;
                  const newZoom = currentZoom + 0.2;
                  mapRef.current?.flyTo({
                    center: clusterCenter,
                    zoom: newZoom,
                    essential: true,
                  });
                }
              });
            }
          }
        });
        mapRef.current?.on("mouseenter", "unclustered-point", () => {
          if (mapRef.current) {
            mapRef.current.setPaintProperty(
              "unclustered-point",
              "circle-color",
              "#ff7f50"
            );
            mapRef.current.getCanvas().style.cursor = "pointer";
          }
        });

        mapRef.current?.on("mouseleave", "unclustered-point", () => {
          if (mapRef.current) {
            mapRef.current.setPaintProperty(
              "unclustered-point",
              "circle-color",
              "#f28cb1"
            );
            mapRef.current.getCanvas().style.cursor = "";
          }
        });

        // Cluster click event
        mapRef.current?.on("click", "unclustered-point", (e) => {
          const features = mapRef.current?.queryRenderedFeatures(e.point, {
            layers: ["unclustered-point"],
          });
          if (features?.length) {
            const geometry = features[0].geometry;
            const clusterId = features[0].properties?.id;
            let clusterCenter: [number, number] =
              geometry.type === "Point"
                ? (geometry as CustomPoint).coordinates
                : [37.7749, -122.4194];

            const currentZoom = mapRef.current?.getZoom() || 10;
            const newZoom = currentZoom + 0.2;
            mapRef.current?.flyTo({
              center: clusterCenter,
              zoom: newZoom,
              essential: true,
            });
              const roundTo4Digits = (num: number) => {
              return Math.round(num * 10000) / 10000;
            };

            const clusterYachts = {
              id: clusterId,
              latitude: roundTo4Digits(clusterCenter[1]),
              longitude: roundTo4Digits(clusterCenter[0]),
            };
            onClusterClick([clusterYachts]);

            if (clusterId && mapRef.current?.getSource("yachts")) {
              (
                mapRef.current.getSource("yachts") as mapboxgl.GeoJSONSource
              ).getClusterLeaves(clusterId, Infinity, 0, (err, leaves) => {
                if (!err && leaves) {
                  const clusterYachts = leaves.map((leaf: any) => ({
                    id: leaf.properties.id,
                    latitude: leaf.geometry.coordinates[1],
                    longitude: leaf.geometry.coordinates[0],
                  }));
                  onClusterClick(clusterYachts);
                }
              });
            }
          }
        });

        // Hide clusters on high zoom level
        mapRef.current?.on("zoom", () => {
          if (mapRef.current) {
            const zoomLevel = mapRef.current.getZoom();

            if (zoomLevel > 14) {
              mapRef.current.setLayoutProperty(
                "clusters",
                "visibility",
                "visible"
              );
              mapRef.current.setLayoutProperty(
                "cluster-count",
                "visibility",
                "visible"
              );
            } else {
              mapRef.current.setLayoutProperty(
                "clusters",
                "visibility",
                "visible"
              );
              mapRef.current.setLayoutProperty(
                "cluster-count",
                "visibility",
                "visible"
              );
            }
          }
        });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mapRef.current?.isStyleLoaded()) {
      const yachtData: FeatureCollection = {
        type: "FeatureCollection",
        features: yachtLocations.map((location) => ({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [location.longitude, location.latitude],
          },
          properties: { id: location.id },
        })),
      };

      (mapRef.current.getSource("yachts") as mapboxgl.GeoJSONSource)?.setData(
        yachtData
      );
      fitMapToBounds(yachtLocations);
    }
  }, [yachtLocations]);

  useEffect(() => {
    if (mapRef?.current && hoveredLocation) {
      mapRef?.current.flyTo({
        center: [hoveredLocation?.longitude, hoveredLocation?.latitude],
        zoom: 10, // fixed zoom level
        essential: true,
      });
    }
  }, [hoveredLocation]);

  useEffect(() => {
    if (clusterYachts.length === 0 && yachtLocations.length > 0) {
      fitMapToBounds(yachtLocations);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterYachts]);

  return <div ref={mapContainerRef} className="map-container" />;
};

export default MapboxMap;
